import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, todayYmd } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Phone, Wrench } from "lucide-react";
import { toast } from "sonner";

const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function AMCCalendar() {
  const [visits, setVisits] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [open, setOpen] = useState(false);

  const load = () => api.get("/amc/visit-schedule").then((r) => setVisits(r.data));
  useEffect(() => { load(); }, []);

  const parsed = useMemo(
    () => visits.map((v) => ({ ...v, _date: v.scheduled_date ? new Date(v.scheduled_date) : null })),
    [visits]
  );

  const dueDays = useMemo(() => parsed.filter((i) => i._date).map((i) => i._date), [parsed]);
  const forSelected = parsed.filter((i) => sameDay(i._date, selected));
  const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
  const in30Days = new Date(startOfToday); in30Days.setDate(in30Days.getDate() + 30);
  const next30 = parsed
    .filter((i) => i._date && i._date >= startOfToday && i._date <= in30Days && i.status !== "completed")
    .sort((a, b) => a._date - b._date);
  const upcoming = parsed
    .filter((i) => i._date && i._date >= startOfToday && i.status !== "completed")
    .sort((a, b) => a._date - b._date)
    .slice(0, 8);

  const markComplete = async (contract_id, visit_id) => {
    await api.patch(`/amc-contracts/${contract_id}/visits/${visit_id}`, { status: "completed" });
    toast.success("Visit marked complete");
    load();
  };

  return (
    <div data-testid="amc-calendar-page">
      <PageHeader
        eyebrow="After-Sales"
        title="AMC Visit Calendar"
        subtitle="All scheduled maintenance visits across active AMC contracts. Click a date to inspect, mark visits done."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="schedule-visit-button">
                <Plus className="w-4 h-4 mr-2" /> Schedule Visit
              </Button>
            </DialogTrigger>
            <ScheduleDialog defaultDate={selected} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-8">
        <div className="lg:col-span-2 flat-card p-6">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ due: dueDays }}
            modifiersClassNames={{ due: "bg-[#002FA7] !text-white font-bold" }}
            className="rounded-none"
            data-testid="amc-visit-calendar"
          />
          <div className="mt-6 border-t border-[#E5E7EB] pt-4">
            <div className="label-eyebrow mb-3">
              {selected.toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
              {" · "}{forSelected.length} visit{forSelected.length === 1 ? "" : "s"}
            </div>
            <div className="space-y-2">
              {forSelected.length === 0 && <div className="text-xs text-[#9CA3AF] italic py-4">Nothing scheduled for this date.</div>}
              {forSelected.map((v) => (
                <div key={v.id} className="border border-[#E5E7EB] p-3 flex items-center justify-between gap-3" data-testid={`visit-${v.id}`}>
                  <input
                    type="checkbox"
                    checked={v.status === "completed"}
                    onChange={() => v.status !== "completed" && markComplete(v.contract_id, v.id)}
                    className="w-4 h-4 accent-[#10B981]"
                    data-testid={`visit-done-${v.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold tracking-tight truncate ${v.status === "completed" ? "line-through text-[#9CA3AF]" : ""}`}>{v.customer_name}</div>
                    <div className="text-xs text-[#4B5563] truncate">{v.notes || "Routine maintenance"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563] mt-1 flex items-center gap-2 flex-wrap">
                      <Phone className="w-3 h-3" /> {v.phone || "—"}
                      <span>· {v.plan}</span>
                      {v.technician_name && <span>· <Wrench className="w-3 h-3 inline" /> {v.technician_name}</span>}
                      {v.status === "completed" && <span className="text-[#10B981]">· done {fmtDate(v.completed_date)}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flat-card p-6">
          <div className="label-eyebrow mb-1" data-testid="next-30-header">Next 30 Days Forecast</div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-3">
            {next30.length} visit{next30.length === 1 ? "" : "s"} between today &amp; {in30Days.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="next-30-list">
            {next30.map((v) => (
              <button
                key={v.id}
                onClick={() => setSelected(v._date)}
                className={`w-full text-left border border-[#E5E7EB] p-3 hover:border-[#002FA7] transition-colors ${sameDay(v._date, selected) ? "bg-[#EFF6FF] border-[#002FA7]" : ""}`}
                data-testid={`forecast-visit-${v.id}`}
              >
                <div className="text-xs label-eyebrow mb-1">{fmtDate(v.scheduled_date)}</div>
                <div className="text-sm font-bold tracking-tight truncate">{v.customer_name}</div>
                <div className="text-xs text-[#4B5563] truncate">{v.plan} · {v.technician_name || "Unassigned"}</div>
                {v.auto_generated && <div className="text-[9px] uppercase tracking-widest text-[#002FA7] mt-1">Auto-scheduled</div>}
              </button>
            ))}
            {next30.length === 0 && <div className="text-xs text-[#9CA3AF] italic py-4" data-testid="next-30-empty">No visits scheduled in the next 30 days.</div>}
          </div>
          {upcoming.length > next30.length && (
            <div className="text-[10px] text-[#4B5563] mt-3 pt-3 border-t border-[#E5E7EB]">
              + {upcoming.length - next30.length} more visit{upcoming.length - next30.length === 1 ? "" : "s"} beyond the 30-day window.
            </div>
          )}
          <Link to="/amc" className="block mt-4 text-xs font-bold text-[#002FA7] uppercase tracking-widest hover:underline">← Back to AMC Contracts</Link>
        </div>
      </div>
    </div>
  );
}

function ScheduleDialog({ defaultDate, onDone }) {
  const [contracts, setContracts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [f, setF] = useState({
    contract_id: "",
    scheduled_date: todayYmd(defaultDate),
    technician_id: "",
    technician_name: "",
    notes: "",
  });
  useEffect(() => {
    api.get("/amc-contracts").then((r) => setContracts(r.data.filter((c) => c.status === "Active")));
    api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "technician" || u.role === "admin")));
  }, []);
  const set = (k) => (v) => setF({ ...f, [k]: v.target ? v.target.value : v });

  const submit = async () => {
    if (!f.contract_id || !f.scheduled_date) { toast.error("Contract and date required"); return; }
    const tech = techs.find((t) => t.id === f.technician_id);
    await api.post(`/amc-contracts/${f.contract_id}/visits`, {
      scheduled_date: f.scheduled_date,
      technician_id: f.technician_id || null,
      technician_name: tech?.name || null,
      notes: f.notes,
    });
    toast.success("Visit scheduled");
    onDone();
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a]">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Schedule AMC Visit</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div>
          <Label className="label-eyebrow">AMC Contract</Label>
          <Select value={f.contract_id} onValueChange={set("contract_id")}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid="visit-contract"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_name} — {c.plan}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">Scheduled Date</Label>
          <Input type="date" value={f.scheduled_date} onChange={set("scheduled_date")} className="rounded-none mt-2 h-10" data-testid="visit-date" />
        </div>
        <div>
          <Label className="label-eyebrow">Technician</Label>
          <Select value={f.technician_id} onValueChange={set("technician_id")}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid="visit-tech"><SelectValue placeholder="Unassigned" /></SelectTrigger>
            <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.role})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Input value={f.notes} onChange={set("notes")} placeholder="Cleaning, inverter check, IV curve…" className="rounded-none mt-2 h-10" data-testid="visit-notes" />
        </div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="visit-submit">Schedule</Button>
      </div>
    </DialogContent>
  );
}
